/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Shared time field styles for assignment and quick schedule forms.
 *
 * @file AetTimeFieldStyles.js
 * @author Anuja Gaikwad
 * @since 2.0.0
 */

import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import mixins from '../../../assets/styles/mixins.scss';
import {
  quickScheduleFormFieldLabelStyles
} from '../../QuickSchedule/quickScheduleFormConstants';

const ASSIGNMENT_FORM_TIME_FIELD = '.assignment-form-time-field';
const QUICK_SCHEDULE_FORM_TIME_FIELD = '.quick-schedule-form-time-field';

const assignmentFormFieldLabelStyles = {
  fontWeight: mixins.fontWeight500,
  fontSize: mixins.fontSize16,
  color: mixins.lightTextColor2,
  lineHeight: '20px',
  paddingBottom: 4
};

export const TimeWrapper = styled(Box)({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },
  [`&&${ASSIGNMENT_FORM_TIME_FIELD}`]: {
    width: '100%'
  },
  [`&&${QUICK_SCHEDULE_FORM_TIME_FIELD}`]: {
    width: '100%'
  }
});

export const Label = styled(Typography)({
  '&&': {
    fontSize: 14,
    fontWeight: mixins.fontWeight400,
    color: mixins.greyScaleColor,
    fontFamily: mixins.fontFamilyPlusSans,
    lineHeight: '22px'
  },
  [`${ASSIGNMENT_FORM_TIME_FIELD} &&`]: assignmentFormFieldLabelStyles,
  [`${QUICK_SCHEDULE_FORM_TIME_FIELD} &&`]: quickScheduleFormFieldLabelStyles(mixins)
});

export const TimeFieldWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hasError' && prop !== 'hasSuccess'
})(({ hasError }) => ({
  '&&': {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: 144,
    height: 48,
    border: `1px solid ${mixins.amethyst}`,
    boxSizing: 'border-box',
    borderRadius: 4
  },
  [`${ASSIGNMENT_FORM_TIME_FIELD} &&`]: {
    width: '100%',
    padding: 12
  },
  [`${QUICK_SCHEDULE_FORM_TIME_FIELD} &&`]: {
    width: '100%',
    height: 48,
    boxSizing: 'border-box',
    border: `1px solid ${mixins.amethyst}`,
    borderRadius: 4,
    padding: 12
  },
  '&&&&': {
    ...(hasError && { border: `2px solid ${mixins.accentRedDark}` })
  }
}));

export const SegmentedInput = styled(Box)({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '75%',
    height: '100%',
    padding: 0,
    boxSizing: 'border-box',
    gap: 0
  },
  [`${ASSIGNMENT_FORM_TIME_FIELD} &&`]: {
    width: 'auto'
  },
  [`${QUICK_SCHEDULE_FORM_TIME_FIELD} &&`]: {
    width: 'auto'
  }
});

export const TimeSegment = styled('input')({
  '&&': {
    border: 'none',
    background: 'transparent',
    outline: 'none',
    padding: 0,
    fontSize: mixins.fontSize16,
    fontFamily: mixins.fontFamilyPlusSans,
    textAlign: 'center',
    width: 28,
    cursor: 'text',
    color: mixins.greyScaleColor
  }
});

export const Separator = styled('span')({
  '&&': {
    fontSize: mixins.fontSize16,
    color: mixins.greyScaleColor,
    fontFamily: mixins.fontFamilyPlusSans
  }
});

export const PeriodButton = styled('button')({
  '&&': {
    border: 'none',
    background: 'transparent',
    outline: 'none',
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    fontFamily: mixins.fontFamilyPlusSans,
    padding: 0,
    margin: 0,
    cursor: 'text',
    color: mixins.timeIconColor,
    borderRadius: 2,
    minWidth: 28,
    textAlign: 'center',
    textTransform: 'none',
    lineHeight: 'normal',
    letterSpacing: 'normal'
  }
});

export const ClockIcon = styled(Box)({
  '&&': {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .MuiSvgIcon-root': {
      width: 18,
      height: 18,
      fontSize: 18,
      color: mixins.timeIconColor
    }
  },
  [`${QUICK_SCHEDULE_FORM_TIME_FIELD} &&`]: {
    '& svg': {
      fontSize: 18,
      width: 18,
      height: 18,
      color: mixins.timeIconColor
    }
  }
});
